import { Box, HStack, Text } from "@chakra-ui/react";
import { Field, FormInput, FormSwitch, RemoveIconButton } from "../../ui/inputs";
import { createEmptyRangeRule } from "../../models/scoringModel";
import { pointsColor } from "../../helpers/pointsColor";

/** Detects overlapping [min, max] ranges (inclusive) among the given rules. Returns a Set of overlapping indices. */
export function findOverlappingRangeIndices(rules) {
    const overlapping = new Set();
    const parsed = (rules ?? []).map((r, i) => ({
        i,
        min: r.min === null || r.min === "" ? -Infinity : Number(r.min),
        max: r.max === null || r.max === "" ? Infinity : Number(r.max),
    }));
    for (let a = 0; a < parsed.length; a++) {
        for (let b = a + 1; b < parsed.length; b++) {
            const r1 = parsed[a];
            const r2 = parsed[b];
            if (r1.min <= r2.max && r2.min <= r1.max) {
                overlapping.add(r1.i);
                overlapping.add(r2.i);
            }
        }
    }
    return overlapping;
}

const RangeRulesEditor = ({ rules, maxScore, onChange, labels, disabled = false }) => {
    const list = rules ?? [];
    const overlapping = findOverlappingRangeIndices(list);
    const update = (i, patch) => onChange(list.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
    const add = () => onChange([...list, createEmptyRangeRule()]);

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Text fontSize="xs" color="gray.500" fontWeight={600} textTransform="uppercase" letterSpacing="wider">
                {labels.valueRulesTitle}
            </Text>
            {list.map((rule, i) => (
                <Box key={i}>
                    <HStack gap={3} align="flex-end">
                        <Box flex={1}>
                            <Field label={labels.rangeFrom}>
                                <FormInput
                                    type="number"
                                    disabled={disabled}
                                    value={rule.min ?? ""}
                                    onChange={(e) => update(i, { min: e.target.value === "" ? null : Number(e.target.value) })}
                                />
                            </Field>
                        </Box>
                        <Box flex={1}>
                            <Field label={labels.rangeTo}>
                                <FormInput
                                    type="number"
                                    disabled={disabled}
                                    value={rule.max ?? ""}
                                    onChange={(e) => update(i, { max: e.target.value === "" ? null : Number(e.target.value) })}
                                />
                            </Field>
                        </Box>
                        <Box flex={1}>
                            <Field label={labels.pointsColumn}>
                                <FormInput
                                    type="number"
                                    disabled={disabled}
                                    value={rule.points ?? ""}
                                    color={pointsColor(rule.points, maxScore)}
                                    onChange={(e) => update(i, { points: e.target.value === "" ? null : Number(e.target.value) })}
                                />
                            </Field>
                        </Box>
                        <FormSwitch
                            label={labels.disqualifyColumn}
                            checked={!!rule.disqualify}
                            onChange={(v) => update(i, { disqualify: v })}
                            disabled={disabled}
                            size="sm"
                        />
                        <RemoveIconButton onClick={() => remove(i)} disabled={disabled} />
                    </HStack>
                    {overlapping.has(i) && (
                        <Text fontSize="xs" color="red.600" mt={1}>
                            {labels.rangeOverlapError}
                        </Text>
                    )}
                </Box>
            ))}
            <Box
                as="button"
                type="button"
                onClick={add}
                disabled={disabled}
                alignSelf="flex-start"
                px={3}
                py={1.5}
                borderRadius={6}
                border="1px dashed"
                borderColor="gray.300"
                fontSize="xs"
                color="gray.500"
                opacity={disabled ? 0.5 : 1}
                cursor={disabled ? "not-allowed" : "pointer"}
                _hover={disabled ? undefined : { borderColor: "purple.400", color: "purple.600" }}
            >
                {labels.addRangeRule}
            </Box>
        </Box>
    );
};

export default RangeRulesEditor;
